import React, { useState, useContext } from 'react';
import { TextField, Button, Typography, Box, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios'; // Para manejar la solicitud HTTP
import logo from '../img/injupemplogogobierno.png';
import sello from '../img/sello.png';
import { useMediaQuery } from '@mui/material';
import { ContextSession } from '../../services/ContextSession';

const ContractForm = () => {
    let sessi = useContext(ContextSession);
    const isMobile = useMediaQuery('(max-width:500px)'); // Detectar si es una pantalla móvil

    const location = useLocation();
    const navigate = useNavigate();
    const { nombre, email, dni, codigo } = location.state || {}; // Accede a los datos desde el state

    const currentDate = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [telefono, setTelefono] = useState('');
    const [domicilio, setDomicilio] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessages, setDialogMessages] = useState([]);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Estado para el CircularProgress

    const handleCheckboxChange = (event) => {
        setTermsAccepted(event.target.checked);
    };

    const handleTelefonoChange = (event) => {
        const value = event.target.value.replace(/\D/g, '');
        setTelefono(value);
    };

    const handleSubmit = async () => {
        let messages = [];

        if (!termsAccepted) messages.push('Debe aceptar los términos y condiciones.');
        if (!telefono) messages.push('Debe ingresar el número de teléfono.');
        if (!domicilio) messages.push('Debe ingresar el domicilio.');
        if (telefono && telefono.length < 8) messages.push('El teléfono debe tener 8 dígitos.');

        if (messages.length > 0) {
            setDialogMessages(messages);
            setDialogOpen(true);
        } else {
            setIsSubmitting(true); // Mostrar el CircularProgress
            try {
                const formData = {
                    domicilio,
                    celular: telefono,
                    dni,
                    codigo,
                };

                const response = await axios.post(`${sessi.ip}/api/contrato/completo/`, formData);

                if (response.status === 200) {
                    setSuccessDialogOpen(true);
                }
            } catch (error) {
                console.error('Error al enviar el contrato:', error);
            } finally {
                setIsSubmitting(false); // Ocultar el CircularProgress
            }
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
    };

    const handleSuccessClose = () => {
        // Navegar a la raíz y eliminar el historial para no poder volver atrás
        navigate('/', { replace: true });
    };

    return (
        <Box sx={{ maxWidth: '800px', margin: 'auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img src={logo} alt="INJUPEMP Logo" style={{ width: '120px', marginBottom: '20px' }} />
            </div>

            <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
                CONTRATO DE SERVICIOS ELECTRÓNICOS
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                EL INSTITUTO NACIONAL DE JUBILACIONES Y PENSIONES DE LOS EMPLEADOS Y FUNCIONARIOS DEL PODER EJECUTIVO (INJUPEMP), denominado de ahora en adelante <b>"<b>EL INSTITUTO</b>"</b> con Registro Tributario Nacional (RTN) número 08019003246690 y por otra parte el Sr.(a)
                <TextField
                    variant="filled"
                    size='small'
                    margin="dense"
                    sx={{ verticalAlign: 'baseline', width: isMobile ? '200px' : '400px', marginLeft: '8px', marginRight: '8px' }}
                    value={nombre} // Mostrar el nombre recibido
                    InputProps={{
                        readOnly: true,
                    }}
                />
                , mayor de edad, con domicilio en
                <TextField
                    variant="standard"
                    placeholder='Ingrese su domicilio aquí'
                    margin="dense"
                    sx={{ verticalAlign: 'baseline', width: isMobile ? '200px' : '450px', marginLeft: '8px', marginRight: '8px' }}
                    value={domicilio}
                    onChange={(e) => setDomicilio(e.target.value)}
                />
                con Documento Nacional de Identificación (DNI)
                <TextField
                    variant="filled"
                    margin="dense"
                    size='small'
                    sx={{ verticalAlign: 'baseline', width: '200px', marginLeft: '8px', marginRight: '8px' }}
                    value={dni} // Mostrar el DNI recibido
                    InputProps={{
                        readOnly: true,
                    }}
                />
                , con correo electrónico
                <TextField
                    variant="filled"
                    margin="dense"
                    size='small'
                    sx={{ verticalAlign: 'baseline', width: isMobile ? '200px' : '350px', marginLeft: '8px', marginRight: '8px' }}
                    value={email} // Mostrar el correo recibido
                    InputProps={{
                        readOnly: true,
                    }}
                />
                y número celular
                <TextField
                    variant="standard"
                    placeholder="Ingrese su teléfono aquí"
                    margin="dense"
                    inputMode="numeric" // Muestra el teclado numérico en dispositivos móviles
                    pattern="[0-9]*"    // Acepta solo números
                    sx={{ verticalAlign: 'baseline', width: '200px', marginLeft: '8px', marginRight: '8px' }}
                    value={telefono}
                    onChange={(e) => {
                        const valor = e.target.value;
                        // Permitir solo números y hasta 8 dígitos
                        if (/^\d*$/.test(valor) && valor.length <= 8) {
                            handleTelefonoChange(e);
                        }
                    }}
                />


                , quien en adelante se denominará <b>"<b>EL PARTICIPANTE AFILIADO</b>"</b>; libre y espontáneamente hemos convenido celebrar, como en efecto así lo hacemos, el presente Contrato de Servicios Electrónicos...
            </Typography>

            <Typography variant="h6" align="center" fontWeight="bold" gutterBottom>
                CLÁUSULA PRIMERA. DEFINICIONES.
            </Typography>

            <Typography variant="body1" align="justify" paragraph>
                <b>1) Servicios Electrónicos:</b> Servicios prestados por canales digitales, a través de internet y mensajería (Correo, Whatsapp, Mensajes de Texto); que por su naturaleza, están automatizados y requieren una intervención humana mínima, que <b>El Instituto</b> desarrolle para prestar servicios a <b><b>EL PARTICIPANTE AFILIADO</b></b> y que los pueda gestionar en la plataforma propiedad de <b>EL INSTITUTO</b>.
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                <b>2) Ingreso a los Servicios Electrónicos:</b> <b><b>EL PARTICIPANTE AFILIADO</b></b> ingresará a la plataforma para realizar las consultas, gestiones, actualizaciones u otros servicios facilitados por <b>EL INSTITUTO</b>, con la identificación del usuario y contraseña asignado por <b>EL INSTITUTO</b>.
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                <b>3) Usuario:</b> Cadena de letras o números únicos que se le asignará al Participante, para uso habitual en la plataforma de <b>EL INSTITUTO</b>.
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                <b> 4) Contraseña:</b> Clave personal de El Usuario, que le permitirá acceder a la plataforma de <b>EL INSTITUTO</b>.
            </Typography>

            <Typography variant="h6" align="center" fontWeight="bold" gutterBottom>
                CLÁUSULA SEGUNDA. DOCUMENTOS A SUSCRIBIR:
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                <b>EL PARTICIPANTE AFILIADO</b>: que desee acceder a los Servicios Electrónicos que presta y que podrá prestar en el futuro <b>EL INSTITUTO</b>, deberá suscribir el "Contrato de Servicios Electrónicos".
            </Typography>

            <Typography variant="h6" align="center" fontWeight="bold" gutterBottom>
                CLÁUSULA TERCERA. RESPONSABILIDADES DEL PARTICIPANTE.
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                Sus responsabilidades son: <b>1)</b> Seguir las medidas de seguridad recomendadas por el Instituto y otras que a su juicio estime convenientes, para evitar el uso indebido de los Servicios Electrónicos por él o terceras personas y los efectos que de este uso podrían derivarse.
                <b>2)</b> Reconoce y acepta el carácter confidencial de la información gestionada por <b>El Instituto bajo el entendido de que la misma es de carácter personal e informativa</b>, esta sin responsabilidad para <b>EL INSTITUTO</b>.
                <b>3)</b> En caso de sospecha de uso indebido del usuario asignado o de la contraseña, <b>EL PARTICIPANTE AFILIADO</b> deberá proceder inmediatamente a realizar la gestión de cambio de contraseña o solicitar el bloqueo del usuario a través de los canales oficiales de atención proporcionados por <b>EL INSTITUTO</b>. Toda información generada y cualquier acción realizada con dicho usuario antes de la notificación formal del bloqueo será de exclusiva responsabilidad de <b>EL PARTICIPANTE AFILIADO</b>.
            </Typography>

            <Typography variant="h6" align="center" fontWeight="bold" gutterBottom>
                CLÁUSULA CUARTA. RESPONSABILIDADES DEL INSTITUTO.
            </Typography>
            <Typography variant="body1" align="justify" paragraph>
                Sus responsabilidades son: <b>1)</b> Afiliar a El Participante a los Servicios Electrónicos. <b>2)</b> Proporcionar a El Participante el usuario y contraseña para el uso de los Servicios Electrónicos de El Instituto.
            </Typography>

            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ position: 'relative', zIndex: 1 }}>
                    {/* Aquí va el texto */}
                    <Typography variant="h6" align="center" fontWeight="bold" gutterBottom>
                        EN TESTIMONIO DE LO CUAL, las partes firman el presente contrato en la ciudad de Tegucigalpa, Francisco Morazán, a fecha <span style={{ fontWeight: 'bold' }}>{currentDate}</span>
                    </Typography>

                    <Typography variant="body1" align="center" paragraph>
                        Instituto de Jubilaciones y Pensiones de los Empleados y Funcionarios del Poder Ejecutivo
                    </Typography>
                </div>

                <img
                    src={sello}
                    alt="Sello"
                    style={{
                        position: 'absolute',
                        width: '120px',
                        opacity: 0.4, // Puedes ajustar la opacidad si quieres que se vea detrás del texto
                        pointerEvents: 'none', // Deshabilita eventos de clic
                        userSelect: 'none',    // Deshabilita la selección de la imagen
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)', // Esto centra la imagen
                    }}
                    onContextMenu={(e) => e.preventDefault()} // Deshabilita clic derecho
                />
            </div>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={termsAccepted}
                        onChange={handleCheckboxChange}
                        sx={{ color: '#AD8401', '&.Mui-checked': { color: '#AD8401' } }}
                    />
                }
                label="Acepto los términos y condiciones descritos en el presente contrato de servicios electrónicos para la creación de mi usuario virtual."
            />

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                {isSubmitting ? (
                    <CircularProgress />
                ) : (
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: '#89CFE0', color: '#fff', '&:hover': { backgroundColor: '#5bb1b6' } }}
                        onClick={handleSubmit}
                        disabled={!termsAccepted}
                    >
                        Enviar Contrato
                    </Button>
                )}
            </div>

            {/* Dialog de Error */}
            <Dialog open={dialogOpen} onClose={handleClose}>
                <DialogTitle style={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorIcon sx={{ color: 'var(--color-brand-secondary)', marginRight: '8px' }} />
                    Complete todos los campos
                </DialogTitle>
                <DialogContent>
                    {dialogMessages.map((message, index) => (
                        <Typography key={index}>-{message}</Typography>
                    ))}
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleClose} style={{
                        backgroundColor: '#89CFE0',
                        borderColor: '#89CFE0',
                        color: '#fff',
                        fontWeight: 'bold'
                    }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Dialog de Éxito */}
            <Dialog open={successDialogOpen} onClose={handleSuccessClose}>
                <DialogTitle style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ color: 'green', marginRight: '8px' }} />
                    Su contrato fue enviado con éxito
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Uno de nuestros oficiales revisará el mismo y recibirá las credenciales de acceso al sistema en su correo electrónico.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleSuccessClose} style={{
                        backgroundColor: '#89CFE0',
                        borderColor: '#89CFE0',
                        color: '#fff',
                        fontWeight: 'bold'
                    }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContractForm;
